"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import {
  FilterSidebar,
  MobileFilterPanel,
  TherapistGrid,
  TherapistSearchHeader
} from "@repo/ui";
import { PublicTherapistProfile } from "@repo/supabase";

interface TherapistsClientProps {
  therapists: PublicTherapistProfile[];
  partnershipId?: string;
  partnershipName?: string | null;
}

export default function TherapistsClient({ therapists, partnershipId, partnershipName }: TherapistsClientProps) {
  const router = useRouter();
  
  // Extract unique values from therapists for dynamic filter options
  // Get actual specializations from therapists' data
  const therapistSpecializations = new Set<string>();
  const therapistModalities = new Set<string>();
  const therapistLanguages = new Set<string>();
  
  therapists.forEach(therapist => {
    // Collect areas of focus
    therapist.areasOfFocus.forEach(category => {
      category.items.forEach(item => {
        therapistSpecializations.add(item);
      });
    });
    
    // Collect modalities
    therapist.modalities.forEach(modality => {
      therapistModalities.add(modality.name);
    });
    
    // Collect languages
    therapist.languages.forEach(language => {
      therapistLanguages.add(language);
    });
  });
  
  const allAreasOfFocus = Array.from(therapistSpecializations).sort();
  const allModalities = Array.from(therapistModalities).sort();
  const allLanguages = Array.from(therapistLanguages).sort();
  
  const filterOptions = {
    therapyType: ["Individual Therapy", "Couples Therapy"],
    languages: allLanguages,
    areasOfFocus: { "All Specializations": allAreasOfFocus },
    provinces: [
      "Alberta",
      "British Columbia", 
      "Manitoba",
      "New Brunswick",
      "Newfoundland and Labrador",
      "Northwest Territories",
      "Nova Scotia",
      "Nunavut",
      "Ontario",
      "Prince Edward Island",
      "Quebec",
      "Saskatchewan",
      "Yukon"
    ].sort(),
    therapistTier: ["Intern Therapist", "Associate Therapist"],
    availability: ["Available Now", "Waitlist"],
    modalities: allModalities
  };

  const [filters, setFilters] = useState({
    therapyType: [] as string[],
    languages: [] as string[],
    areasOfFocus: [] as string[],
    provinces: [] as string[],
    therapistTier: [] as string[],
    availability: [] as string[],
    serviceFormat: [] as string[],
    modalities: [] as string[]
  });
  const [focusSearchTerm, setFocusSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Toggle filter selection
  const toggleFilter = (category: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      therapyType: [],
      languages: [],
      areasOfFocus: [],
      provinces: [],
      therapistTier: [],
      availability: [],
      serviceFormat: [],
      modalities: []
    });
    setFocusSearchTerm("");
  };

  // Filter therapists based on selected filters
  const filteredTherapists = therapists.filter(therapist => {
    // Check therapy type
    if (filters.therapyType.length > 0) {
      const therapistTypes: string[] = [];
      // All therapists offer individual therapy
      therapistTypes.push("Individual Therapy");
      // Check if they are approved for couples therapy (show even if not currently accepting)
      if (therapist.isApprovedForCouples) {
        therapistTypes.push("Couples Therapy");
      }
      const hasType = filters.therapyType.some(type =>
        therapistTypes.includes(type)
      );
      if (!hasType) return false;
    }

    // Check languages
    if (filters.languages.length > 0) {
      const hasLanguage = filters.languages.some(language => 
        therapist.languages.includes(language)
      );
      if (!hasLanguage) return false;
    }

    // Check areas of focus
    if (filters.areasOfFocus.length > 0) {
      const therapistAreas = therapist.areasOfFocus.flatMap(cat => cat.items);
      const hasArea = filters.areasOfFocus.some(area => 
        therapistAreas.includes(area)
      );
      if (!hasArea) return false;
    }

    // Check provinces - for now all therapists serve across Canada
    // This would need to be updated based on actual service areas
    if (filters.provinces.length > 0) {
      // Skip province filtering for now since all serve across Canada
    }

    // Check therapist tier
    if (filters.therapistTier.length > 0) {
      const tierMatch = filters.therapistTier.some(tier => {
        if (tier === "Intern Therapist" && therapist.therapistTier === "resident") return true;
        if (tier === "Associate Therapist" && therapist.therapistTier === "associate") return true;
        return false;
      });
      if (!tierMatch) return false;
    }

    // Check availability
    if (filters.availability.length > 0) {
      const isAvailable = therapist.availability.status === "Accepting new clients";
      const availMatch = filters.availability.some(avail => {
        if (avail === "Available Now" && isAvailable) return true;
        if (avail === "Waitlist" && !isAvailable) return true;
        return false;
      });
      if (!availMatch) return false;
    }

    // Check modalities
    if (filters.modalities.length > 0) {
      const therapistModalities = therapist.modalities.map(m => m.name);
      const hasModality = filters.modalities.some(modality => 
        therapistModalities.includes(modality)
      );
      if (!hasModality) return false;
    }

    return true;
  });

  // Count active filters
  const activeFilterCount = Object.values(filters).reduce((acc, filterArray) => acc + filterArray.length, 0);

  // Map therapists to the format expected by TherapistGrid
  const gridTherapists = filteredTherapists.map(therapist => ({
    id: therapist.id,
    slug: therapist.slug,
    firstName: therapist.firstName,
    lastName: therapist.lastName,
    displayName: therapist.displayName,
    credential: therapist.credential || 'Intern Therapist',
    profileImage: therapist.profileImage,
    therapistTier: therapist.therapistTier,
    acceptingClients: therapist.availability.status === "Accepting new clients",
    acceptingFor: therapist.availability.acceptingFor || [],
    specializations: therapist.areasOfFocus.slice(0, 3).flatMap(cat => cat.items.slice(0, 2)),
    modalities: therapist.modalities.slice(0, 3).map(m => m.name)
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50/30 to-white">
      {/* Partnership Banner */}
      {partnershipName && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-green-900 font-medium">
                Showing all therapists covered by your {partnershipName} partnership
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <TherapistSearchHeader
        resultCount={filteredTherapists.length}
        showCount={activeFilterCount > 0}
        onOpenMobileFilters={() => setShowMobileFilters(true)}
      />

      {/* Mobile Filter Panel */}
      <MobileFilterPanel
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filters={filters}
        filterOptions={filterOptions}
        focusSearchTerm={focusSearchTerm}
        setFocusSearchTerm={setFocusSearchTerm}
        toggleFilter={toggleFilter}
        activeFilterCount={activeFilterCount}
        onClearAll={clearAllFilters}
      />

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-80 flex-shrink-0">
            <FilterSidebar
              filters={filters}
              filterOptions={filterOptions}
              focusSearchTerm={focusSearchTerm}
              setFocusSearchTerm={setFocusSearchTerm}
              toggleFilter={toggleFilter}
              activeFilterCount={activeFilterCount}
              onClearAll={clearAllFilters}
            />
          </aside>

          {/* Results */}
          <div className="flex-1">
            <TherapistGrid 
              therapists={gridTherapists}
              onTherapistClick={(therapist) => {
                // Find the full therapist data to get the slug
                const fullTherapist = filteredTherapists.find(t => t.id === therapist.id);
                if (fullTherapist) {
                  const url = partnershipId
                    ? `/therapist/${fullTherapist.slug}?partnershipId=${partnershipId}`
                    : `/therapist/${fullTherapist.slug}`;
                  router.push(url);
                }
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
}